import { SlidersHorizontal } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { useSubmissionsFilterStore } from "../stores";
import { DILEMMAS_STATUS } from "../constants";
import {
  ThemeFilterGroup,
  StatusFilterGroup,
  DateFilterGroup,
  LanguageFilterGroup,
} from "./filter-groups";
import { memo, useState } from "react";

const SubmissionsFilterComponent = memo(function SubmissionsFilterComponent() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const {
    statusFilter,
    ordering,
    languageFilter,
    themeFilter,
    setStatusFilter,
    setOrdering,
    setLanguageFilter,
    setThemeFilter,
  } = useSubmissionsFilterStore();

  const hasActiveFilters =
    statusFilter !== null ||
    ordering !== null ||
    languageFilter !== null ||
    themeFilter !== null;

  const handleThemeChange = (themeId: string | null) => {
    setThemeFilter(themeId);
    setOpen(false);
  };

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button className="relative bg-primary/10 text-primary hover:bg-primary/20 lg:bg-secondary lg:hover:bg-secondary/90 lg:text-gray-500">
          <SlidersHorizontal />
          <span className="hidden lg:inline">{t("keys.filters")}</span>
          {hasActiveFilters && (
            <span className="absolute -top-1 -right-1 size-2.5 rounded-full bg-primary" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="-translate-x-3 sm:-translate-x-6 w-56">
        <ThemeFilterGroup
          themeFilter={themeFilter}
          onThemeChange={handleThemeChange}
        />
        <DropdownMenuSeparator />
        <StatusFilterGroup
          statusFilter={statusFilter}
          onStatusChange={setStatusFilter}
          options={[
            {
              value: DILEMMAS_STATUS.DRAFT,
              label: t("modules.dilemmas.keys.status_draft"),
              variant: "secondary",
            },
            {
              value: DILEMMAS_STATUS.SCHEDULED,
              label: t("modules.dilemmas.keys.status_scheduled"),
              variant: "dilemma_scheduled",
            },
            {
              value: DILEMMAS_STATUS.PUBLISHED,
              label: t("modules.dilemmas.keys.status_published"),
              variant: "dilemma_published",
            },
            {
              value: DILEMMAS_STATUS.ARCHIVED,
              label: t("modules.dilemmas.keys.status_archived"),
              variant: "dilemma_archived",
            },
            {
              value: DILEMMAS_STATUS.SUBMITTED,
              label: t("modules.dilemmas.keys.status_submitted"),
              variant: "dilemma_submitted",
            },
          ]}
        />
        <DropdownMenuSeparator />
        <DateFilterGroup ordering={ordering} onOrderingChange={setOrdering} />
        <DropdownMenuSeparator />
        <LanguageFilterGroup
          languageFilter={languageFilter}
          onLanguageChange={setLanguageFilter}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
});

export default SubmissionsFilterComponent;
